import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import styles from '../Event.scss';
import moment from 'moment-timezone';
import { Icon } from 'semantic-ui-react';
import DateUtils from '../../../lib/utils/date';
import { GlobalNotifications, Intent, Wizard } from '../../Modules';
import UpdateEventDate from './UpdateEventDate';

export default class EventDates extends React.Component {
  static propTypes = {
    event: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.onInvalidSubmit = this.onInvalidSubmit.bind(this);
  }

  onValidSubmit(model, resetForm, invalidateForm, successCb) {
    if (new Date(model.end_at) < new Date(model.start_at)) {
      GlobalNotifications.show({
        message: 'Unable to update event information',
        intent: Intent.DANGER
      });
      invalidateForm({ end_at: 'Event must end later than it starts' });
    } else {
      this.updateEvent(model, successCb);
    }
  }

  onInvalidSubmit() {
    GlobalNotifications.show({
      message: 'Unable to update event information',
      intent: Intent.DANGER
    });
  }

  updateEvent(model, successCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update event times',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated event times',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
    };

    const event = nullifyEmptyStrings(
      DateUtils.omitTimeZoneWithoutTimes(model)
    );

    const mutation = graphql`
      mutation EventDates_UpdateEventMutation($input: UpdateEventInput!) {
        updateEvent(input: $input) {
          event {
            id
            start_at
            end_at
            time_zone
          }
        }
      }
    `;

    const { start_at, end_at, time_zone } = event;
    const optimisticResponse = {
      updateEvent: {
        event: { id: this.props.event.id, start_at, end_at, time_zone }
      }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.event.id,
          start_at,
          end_at,
          time_zone
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  render() {
    const { event, relay, viewer } = this.props;
    const timeZone =
      event &&
      (event.time_zone || (event.user_event && event.user_event.time_zone));
    const startAtString = (() => {
      if (event && event.start_at) {
        return event.start_at;
      } else if (event && event.user_event && event.user_event.start_at) {
        return event.user_event.start_at;
      }
      return null;
    })();

    const endAtString = (() => {
      if (event && event.end_at) {
        return event.end_at;
      } else if (event && event.user_event && event.user_event.end_at) {
        return event.user_event.end_at;
      }
      return null;
    })();
    // Since user_event does not return all_day, we infer property based on
    // the format of the date string returned (eg. `2020-01-01` infers
    // a Date while `2020-01-01T00:00:00Z` infers a DateTime)
    const allDay = startAtString && startAtString.length === 10;
    const startAt = DateUtils.isoStringToMoment(
      startAtString,
      allDay,
      timeZone
    );
    const endAt = DateUtils.isoStringToMoment(endAtString, allDay, timeZone);
    const hasDates = startAtString && endAtString;
    const hasEventDates = event.start_at && event.end_at;
    const multiDay = !(
      (hasDates && startAt.isSame(endAt, 'day')) ||
      (startAt.isSame(moment(endAt).subtract(6, 'hours'), 'day') && !allDay)
    );
    const trigger = (
      <div
        className={[
          styles.publicDates,
          hasEventDates ? styles.pointer : ''
        ].join(' ')}
        onClick={this.onOpen}
      >
        <Icon
          name='calendar outline alternate'
          style={{ paddingRight: '1em' }}
        />
        {`${startAt.format('LL')}${multiDay ? ` - ${endAt.format('LL')}` : ''}`}
      </div>
    );
    const steps = [
      {
        title: 'Update Event Times',
        component: UpdateEventDate,
        componentProps: { relay, viewer, event },
        formProps: {
          onValidSubmit: this.onValidSubmit,
          onInvalidSubmit: this.onInvalidSubmit
        },
        actions: {
          primary: {
            content: 'Save',
            type: 'submit'
          }
        }
      }
    ];
    return (
      <span>
        {hasDates &&
          <span>
            {!hasEventDates && trigger}
            {hasEventDates &&
              <Wizard
                steps={steps}
                trigger={trigger}
                onOpen={this.onOpen}
                onClose={this.onClose}
              />}
          </span>}
      </span>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/EventDates.js